import { MoreVertical } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { navigateToGameUrl } from '../../utils/lobbyMyGamesNavigation';
import { formatMyGamesStartDisplay } from '../../utils/lobbyGameDisplay';
import { getMyGamesSportLogoPath } from '../../utils/myGamesSportLogo';
import {
  bestBingoTier,
  bingoPrizePoolDisplay,
  bingoTierLabel,
  bingoTileProgress,
  bingoWeekLabel,
  isBingoContestComplete,
  isBingoGameLive,
  type BingoMyGame,
} from '../../utils/lobbyMyGamesBingo';

interface LobbyBingoCardProps {
  game: BingoMyGame;
  /** Full public URL with token appended (or null if unavailable). */
  publicUrl: string | null;
}

/**
 * A Game Grid (Bingo) contest on the My Games tab.
 *
 * Laid out like the Survivor and Pick'em cards it sits beside — logo, title, start line, then a
 * divider and a stat row — so the three read as one list rather than three designs. What differs
 * is the middle column: a Bingo card's progress is how many of its tiles have hit, which is the
 * one number that means anything while a game is on.
 *
 * THE RESULT COLUMN IS THE POINT. A player checking My Games wants to know whether they won, and
 * Bingo answers that in tiers rather than a score: the card pays on the best PATTERN completed,
 * so "2 Lines" is the result, not "18/24". Green for a completed tier, red only once the contest
 * is over with nothing completed — a card that has not won YET is not a loss, and painting it red
 * mid-contest would tell a player they are out while their board is still live.
 */
export default function LobbyBingoCard({ game, publicUrl }: LobbyBingoCardProps) {
  const isComplete = isBingoContestComplete(game);
  const isLive = !isComplete && isBingoGameLive(game);
  const logoPath = getMyGamesSportLogoPath(game.sport ?? undefined);

  const startDisplay = formatMyGamesStartDisplay(game.startUtc);
  const weekDisplay = bingoWeekLabel(game.title);

  const { hit, total } = bingoTileProgress(game);
  const bestTier = bestBingoTier(game);
  const bestTierLabel = bingoTierLabel(bestTier);

  /*
   * Three states, and the middle one is the one worth being careful about. A won tier is green
   * whenever it happened, because a completed pattern cannot be taken back. "No Win" is red only
   * on a finished contest. Everything still running is neutral grey — including a board with no
   * tiers yet, which is the ordinary state of a card in the first quarter.
   */
  const result: { text: string; tone: 'win' | 'loss' | 'neutral' } = bestTierLabel
    ? { text: bestTierLabel, tone: 'win' }
    : isComplete
      ? { text: 'No Win', tone: 'loss' }
      : { text: isLive ? 'In Play' : 'Not Started', tone: 'neutral' };

  const resultTextClass =
    result.tone === 'win'
      ? 'text-[#53d337]'
      : result.tone === 'loss'
        ? 'text-[#ff6b6b]'
        : 'text-[#ABABAB]';

  const handleNavigate = (e?: MouseEvent | KeyboardEvent) => {
    if (!publicUrl) return;
    e?.preventDefault();
    navigateToGameUrl(publicUrl);
  };

  return (
    <a
      href={publicUrl ?? undefined}
      role={publicUrl ? 'link' : undefined}
      tabIndex={publicUrl ? 0 : -1}
      aria-disabled={!publicUrl}
      data-testid={`my-games-bingo-${game.pickemId}`}
      onClick={handleNavigate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleNavigate(e);
      }}
      className={`block rounded-xl p-4 border border-[#373737] bg-[#242424] transition-all duration-200 ${
        publicUrl
          ? 'hover:ring-2 hover:ring-[#53d337]/30 cursor-pointer active:scale-[0.98]'
          : 'cursor-default pointer-events-none'
      }`}
    >
      {/* Row 1: Logo + Title + Menu */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-[#373737] flex items-center justify-center">
            {logoPath ? (
              <img
                src={logoPath}
                alt={`${game.sport ?? ''} logo`}
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <span className="text-zinc-400 font-medium text-[10px]">Logo</span>
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-semibold text-sm text-white tracking-wide uppercase leading-tight">
              {game.title}
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-zinc-500 text-sm font-normal">Start:</span>
              <span className="text-[#53d337] font-semibold text-sm">{startDisplay}</span>
              {weekDisplay && (
                <span className="text-zinc-500 text-sm font-normal">({weekDisplay})</span>
              )}
            </div>
          </div>
        </div>
        <MoreVertical className="w-5 h-5 text-zinc-400 flex-shrink-0" />
      </div>

      {/* Divider */}
      <div className="border-t-2 border-[#373737]/60 mb-4" />

      {/* Three-up stat row — matches the Pick'em card beside it. */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-col gap-1 text-left border-r border-[#6F6F6F] pr-4">
          <span className="text-xs text-zinc-400">Tiles Hit</span>
          <span
            data-testid={`my-games-bingo-tiles-${game.pickemId}`}
            className="text-sm font-bold text-white"
          >
            {total > 0 ? `${hit}/${total}` : '—'}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-center border-r border-[#6F6F6F] pr-4">
          <span className="text-xs text-zinc-400">Prize Pool</span>
          <span
            data-testid={`my-games-bingo-prize-${game.pickemId}`}
            className="text-sm font-bold text-[#53d337]"
          >
            {bingoPrizePoolDisplay(game)}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-right">
          <span className="text-xs text-zinc-400">Result</span>
          <span
            data-testid={`my-games-bingo-result-${game.pickemId}`}
            className={`text-sm font-bold ${resultTextClass}`}
          >
            {result.text}
          </span>
        </div>
      </div>

      {/*
       * Every tier the card has won, when it has won more than one. The stat row headlines the
       * best one because that is what pays; this says what else completed on the way, which is
       * what a player actually wants to see on a finished board.
       */}
      {(game.card.outcome?.payable?.length ?? 0) > 1 && (
        <div
          data-testid={`my-games-bingo-tiers-${game.pickemId}`}
          className="mt-3 flex flex-wrap justify-center gap-1.5"
        >
          {game.card.outcome.payable.map(kind => {
            const label = bingoTierLabel(kind);
            if (!label) return null;
            return (
              <span
                key={kind}
                className="rounded-full border border-[#53d337]/40 bg-[#53d337]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#53d337]"
              >
                {label}
              </span>
            );
          })}
        </div>
      )}
    </a>
  );
}
